/**
 * Service for subscription display formatting and helpers
 */
export class SubscriptionDisplayService {
  /**
   * Get status color based on subscription type
   * @param type - Subscription type
   * @returns CSS color class
   */
  static getStatusColor(type: string): string {
    switch(type) {
      case 'premium':
        return 'premium';
      case 'trial':
        return 'trial';
      default:
        return 'free';
    }
  }

  /**
   * Get status label for display
   * @param type - Subscription type
   * @returns Display label in Korean
   */
  static getStatusLabel(type: string): string {
    switch(type) {
      case 'premium':
        return '프리미엄';
      case 'trial':
        return '체험판';
      case 'beta':
        return 'BETA';
      default:
        return '무료';
    }
  }

  /**
   * Format date for display
   * @param dateString - ISO date string
   * @returns Formatted date in Korean locale
   */
  static formatDate(dateString: string | null | undefined): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      
      return new Intl.DateTimeFormat('ko-KR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(date);
    } catch {
      return '';
    }
  }

  /**
   * Get usage percentage
   * @param current - Current usage count
   * @param limit - Usage limit
   * @returns Percentage (0-100)
   */
  static getUsagePercentage(current: number, limit: number): number {
    if (limit === 0) return 0;
    return Math.min(100, Math.round((current / limit) * 100));
  }

  /**
   * Format usage display text
   * @param current - Current usage
   * @param limit - Usage limit
   * @param isUnlimited - Whether the limit is unlimited
   * @returns Formatted usage string
   */
  static formatUsageDisplay(current: number, limit: number, isUnlimited: boolean = false): string {
    if (isUnlimited) {
      return `${current} / 무제한`;
    }
    return `${current} / ${limit}`;
  }

  /**
   * Get progress bar color based on usage percentage
   * @param percentage - Usage percentage
   * @returns CSS color class
   */
  static getProgressBarColor(percentage: number): string {
    if (percentage >= 90) return 'danger';
    if (percentage >= 70) return 'warning';
    return 'success';
  }
}